import React, { useState, useEffect, useMemo, useRef } from 'react';
import './FallbackPlaylist.css';
import MediaManager from '../MediaManager';
import axios from 'axios';

function FallbackPlaylist({ selectedDevice, site, isAdmin = false }) {
  const [morningPlaylist, setMorningPlaylist] = useState([]);
  const [afternoonPlaylist, setAfternoonPlaylist] = useState([]);
  const [eveningPlaylist, setEveningPlaylist] = useState([]);
  const [selection, setSelection] = useState({ timeOfDay: null, index: null });

  const [savedPlaylists, setSavedPlaylists] = useState([]);
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [linkCheck, setLinkCheck] = useState({
    checking: false,
    message: '',
  });
  const fallbackLoadVersionRef = useRef(0);

  const token = localStorage.getItem('token');
  const deviceId = selectedDevice ? selectedDevice.id : null;
  const deviceType = selectedDevice ? selectedDevice.type : 'Music Player';
  const siteId = site?.id || selectedDevice?.site_id || selectedDevice?.siteId || null;
  const defaultPlaylistTargets = [
    { type: 'Music Player', label: 'Music Default Playlist' },
    { type: 'TV', label: 'TV Default Playlist' },
  ];

  const applyPlaylists = (data = {}) => {
    setMorningPlaylist(data.morning || []);
    setAfternoonPlaylist(data.afternoon || []);
    setEveningPlaylist(data.evening || []);
    setSelection({ timeOfDay: null, index: null });
  };

  // Fetch fallback playlists
  useEffect(() => {
    if (!deviceId) return;

    const fetchFallbackPlaylists = async () => {
      const loadVersion = fallbackLoadVersionRef.current;
      try {
        const response = await axios.get(`/api/playlists/fallback?deviceId=${deviceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (loadVersion !== fallbackLoadVersionRef.current) return;
        const data = response.data;
        applyPlaylists(data);
      } catch (error) {
        if (loadVersion !== fallbackLoadVersionRef.current) return;
        console.error('Error fetching fallback playlists:', error);
        alert('Failed to fetch fallback playlists.');
      }
    };

    fallbackLoadVersionRef.current += 1;
    setCurrentPlaylist(null);
    fetchFallbackPlaylists();
  }, [deviceId, token]);

  // Fetch saved playlists for Open button
  useEffect(() => {
    if (!deviceType) return;

    const fetchPlaylists = async () => {
      try {
        const response = await axios.get(`/api/playlists?deviceType=${deviceType}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const playlists = response.data.playlists || [];
        const transformed = playlists.map((p) => ({
          ...p,
          tracksCount: p.items.length,
        }));
        setSavedPlaylists(transformed);
      } catch (error) {
        console.error('Error fetching playlists:', error);
      }
    };

    fetchPlaylists();
  }, [deviceType, token]);

  const handleAddToFallback = (videos, timeOfDay) => {
    const newVideos = videos.map((video) => ({
      content_id: video.content_id,
      content_type: video.content_type || 'youtube',
      title: video.title,
      thumbnail_url: video.preview_api_url || video.thumbnail_url || video.thumbnail_api_url || null,
      thumbnail_api_url: video.preview_api_url || video.thumbnail_api_url || null,
      duration: video.duration || null,
    }));

    if (timeOfDay === 'Morning') {
      setMorningPlaylist([...morningPlaylist, ...newVideos]);
    } else if (timeOfDay === 'Afternoon') {
      setAfternoonPlaylist([...afternoonPlaylist, ...newVideos]);
    } else if (timeOfDay === 'Evening') {
      setEveningPlaylist([...eveningPlaylist, ...newVideos]);
    }
  };

  const handleSaveFallbackPlaylists = async () => {
    const playlists = {
      morning: morningPlaylist,
      afternoon: afternoonPlaylist,
      evening: eveningPlaylist,
    };

    try {
      await axios.post(
        '/api/playlists/fallback',
        { deviceId, playlists },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Fallback Playlists have been saved!');
    } catch (error) {
      console.error('Error saving fallback playlists:', error);
      alert('Failed to save fallback playlists.');
    }
  };

  const handleSaveAsDefaults = async (defaultsDeviceType) => {
    const playlists = {
      morning: morningPlaylist,
      afternoon: afternoonPlaylist,
      evening: eveningPlaylist,
    };

    try {
      await axios.post(
        '/api/playlists/global-defaults',
        { defaultsDeviceType, playlists },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`${defaultsDeviceType === 'TV' ? 'TV' : 'Music'} default fallback playlists updated!`);
    } catch (error) {
      console.error('Error saving default playlists:', error);
      alert('Failed to save default playlists.');
    }
  };

  const handleLoadDefaults = async (defaultsDeviceType) => {
    if (!deviceId) {
      alert('Select a device before loading defaults.');
      return;
    }

    if (
      !window.confirm(
        `This will replace this device's current fallback playlists with the ${defaultsDeviceType === 'TV' ? 'TV' : 'Music'} defaults. Continue?`
      )
    ) {
      return;
    }

    try {
      fallbackLoadVersionRef.current += 1;
      const response = await axios.post(
        '/api/playlists/defaults/load',
        { deviceId, defaultsDeviceType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      applyPlaylists(response.data);
      alert(`${defaultsDeviceType === 'TV' ? 'TV' : 'Music'} default playlists loaded.`);
    } catch (error) {
      console.error('Error loading default playlists:', error);
      alert('Failed to load default playlists.');
    }
  };

  const getPlaylistFor = (timeOfDay) => {
    if (timeOfDay === 'Morning') return morningPlaylist;
    if (timeOfDay === 'Afternoon') return afternoonPlaylist;
    if (timeOfDay === 'Evening') return eveningPlaylist;
    return [];
  };

  const setPlaylistFor = (timeOfDay, next) => {
    if (timeOfDay === 'Morning') setMorningPlaylist(next);
    if (timeOfDay === 'Afternoon') setAfternoonPlaylist(next);
    if (timeOfDay === 'Evening') setEveningPlaylist(next);
  };

  const selectTrack = (timeOfDay, index) => {
    if (linkCheck.checking) return;
    setSelection((prev) => {
      const isSame = prev.timeOfDay === timeOfDay && prev.index === index;
      return isSame ? { timeOfDay: null, index: null } : { timeOfDay, index };
    });
  };

  const moveSelected = (direction) => {
    if (!selection.timeOfDay || selection.index === null) return;
    const timeOfDay = selection.timeOfDay;
    const index = selection.index;
    const playlist = [...getPlaylistFor(timeOfDay)];
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= playlist.length) return;
    [playlist[index], playlist[nextIndex]] = [playlist[nextIndex], playlist[index]];
    setPlaylistFor(timeOfDay, playlist);
    setSelection({ timeOfDay, index: nextIndex });
  };

  const removeSelected = () => {
    if (!selection.timeOfDay || selection.index === null) return;
    const timeOfDay = selection.timeOfDay;
    const index = selection.index;
    const playlist = [...getPlaylistFor(timeOfDay)];
    if (!playlist[index]) return;
    playlist.splice(index, 1);
    setPlaylistFor(timeOfDay, playlist);
    setSelection({ timeOfDay: null, index: null });
  };

  const checkSelectedPlaylistLinks = async () => {
    if (!selection.timeOfDay || selection.index === null || linkCheck.checking) return;

    const timeOfDay = selection.timeOfDay;
    const playlist = getPlaylistFor(timeOfDay);
    const youtubeItems = playlist
      .map((item, index) => ({ ...item, index }))
      .filter((item) => (item.content_type || 'youtube').toLowerCase() === 'youtube');

    if (youtubeItems.length === 0) {
      const message = `${timeOfDay} playlist has no YouTube links to check.`;
      setLinkCheck({ checking: false, message });
      alert(message);
      return;
    }

    setLinkCheck({
      checking: true,
      message: `Checking ${youtubeItems.length} YouTube link${youtubeItems.length === 1 ? '' : 's'} in ${timeOfDay}...`,
    });

    try {
      const response = await axios.post(
        '/api/youtube/check-videos',
        {
          siteId,
          videos: youtubeItems.map((item) => ({
            content_id: item.content_id,
            title: item.title,
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const results = response.data?.results || [];
      const failed = results.filter((item) => !item.playable);
      const failedIndexes = new Set(
        failed
          .map((item) => youtubeItems[item.index]?.index)
          .filter((index) => Number.isInteger(index))
      );

      if (failedIndexes.size > 0) {
        const nextPlaylist = playlist.filter((_, index) => !failedIndexes.has(index));
        setPlaylistFor(timeOfDay, nextPlaylist);
        setSelection({ timeOfDay: null, index: null });
      }

      const removedTitles = failed.slice(0, 6).map((item) => {
        const title = item.title || item.content_id || 'Untitled video';
        return `${title} (${item.reason || 'Unavailable'})`;
      });
      const extraRemoved = failed.length > removedTitles.length
        ? ` ${failed.length - removedTitles.length} more removed.`
        : '';
      const message =
        failed.length > 0
          ? `${timeOfDay} check complete: ${results.length} checked, ${failed.length} removed. Removed: ${removedTitles.join('; ')}.${extraRemoved}`
          : `${timeOfDay} check complete: ${results.length} checked, none removed.`;

      setLinkCheck({ checking: false, message });
      alert(message);
    } catch (error) {
      console.error('Error checking fallback playlist links:', error);
      const message = error.response?.data?.error || 'Failed to check playlist links.';
      setLinkCheck({ checking: false, message });
      alert(message);
    }
  };

  const shuffleSelectedPlaylist = () => {
    if (!selection.timeOfDay || selection.index === null) return;
    const timeOfDay = selection.timeOfDay;
    const playlist = [...getPlaylistFor(timeOfDay)];
    if (playlist.length < 2) return;

    for (let i = playlist.length - 1; i > 0; i -= 1) {
      const randomIndex = Math.floor(Math.random() * (i + 1));
      [playlist[i], playlist[randomIndex]] = [playlist[randomIndex], playlist[i]];
    }

    setPlaylistFor(timeOfDay, playlist);
    setSelection({ timeOfDay: null, index: null });
  };

  // Handlers for the MediaManager operations (Open, Save As, Save, Delete, Clear)
  const onSavePlaylist = async (playlistItems, nameOverride = null) => {
    if (playlistItems.length === 0) {
      alert('Cannot save an empty playlist.');
      return;
    }

    const playlistName =
      typeof nameOverride === 'string' && nameOverride.trim()
        ? nameOverride.trim()
        : prompt('Enter a name for the new playlist:');
    if (playlistName) {
      try {
        const response = await axios.post(
          '/api/playlists',
          {
            name: playlistName,
            items: playlistItems,
            deviceType: deviceType,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Playlist saved!');
        const newPlaylist = {
          id: response.data.playlistId,
          name: playlistName,
          media_type: deviceType.toLowerCase(),
          items: playlistItems,
          tracksCount: playlistItems.length,
        };
        setSavedPlaylists([...savedPlaylists, newPlaylist]);
        setCurrentPlaylist(newPlaylist);
      } catch (error) {
        console.error('Error saving playlist:', error);
        alert('Failed to save playlist.');
      }
    }
  };

  const onUpdatePlaylist = async (updatedItems) => {
    if (!currentPlaylist) {
      alert('No playlist selected for updating.');
      return;
    }

    try {
      await axios.put(
        `/api/playlists/${currentPlaylist.id}`,
        {
          name: currentPlaylist.name,
          items: updatedItems,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Playlist updated successfully');
      setSavedPlaylists(
        savedPlaylists.map((p) =>
          p.id === currentPlaylist.id ? { ...p, items: updatedItems, tracksCount: updatedItems.length } : p
        )
      );
      setCurrentPlaylist({
        ...currentPlaylist,
        items: updatedItems,
      });
    } catch (error) {
      console.error('Error updating playlist:', error);
      alert('Failed to update playlist.');
    }
  };

  const onDeletePlaylist = async () => {
    if (!currentPlaylist) {
      alert('No playlist selected to delete.');
      return;
    }
    const playlistId = currentPlaylist.id;
    if (!window.confirm('Are you sure you want to delete this playlist?')) return;

    try {
      await axios.delete(`/api/playlists/${playlistId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Playlist deleted successfully');
      setSavedPlaylists(savedPlaylists.filter((p) => p.id !== playlistId));
      setCurrentPlaylist(null);
    } catch (error) {
      console.error('Error deleting playlist:', error);
      alert('Failed to delete playlist.');
    }
  };

  const onLoadSavedPlaylist = (p) => {
    setCurrentPlaylist({
      id: p.id,
      name: p.name,
      items: p.items || [],
    });
  };

  const existingPlaylist = useMemo(
    () => (currentPlaylist ? currentPlaylist.items : []),
    [currentPlaylist]
  );

  const clearPlaylist = (timeOfDay) => {
    if (timeOfDay === 'Morning') {
      setMorningPlaylist([]);
    } else if (timeOfDay === 'Afternoon') {
      setAfternoonPlaylist([]);
    } else if (timeOfDay === 'Evening') {
      setEveningPlaylist([]);
    }
    setSelection({ timeOfDay: null, index: null });
  };

  const selectedTimeOfDay = selection.timeOfDay;
  const selectedIndex = selection.index;
  const selectedPlaylist = selectedTimeOfDay ? getPlaylistFor(selectedTimeOfDay) : [];

  return (
    <div className="fallback-playlist">
      <h3>Fallback Playlists</h3>
      <button onClick={handleSaveFallbackPlaylists}>Save Playlists</button>
      {isAdmin && (
        <div className="fallback-default-actions" aria-label="Default playlist actions">
          {defaultPlaylistTargets.map((target) => (
            <React.Fragment key={target.type}>
              <button onClick={() => handleSaveAsDefaults(target.type)}>
                Save {target.label}
              </button>
              <button onClick={() => handleLoadDefaults(target.type)}>
                Load {target.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="playlists-container">
        {['Morning', 'Afternoon', 'Evening'].map((timeOfDay) => {
          const playlist =
            timeOfDay === 'Morning'
              ? morningPlaylist
              : timeOfDay === 'Afternoon'
              ? afternoonPlaylist
              : eveningPlaylist;

          return (
            <div key={timeOfDay} className="playlist-section">
              <h4>{timeOfDay}</h4>
              {playlist.length > 0 ? (
                <ul>
                  {playlist.map((video, index) => (
                    <li
                      key={index}
                      className={
                        selectedTimeOfDay === timeOfDay && selectedIndex === index ? 'selected' : ''
                      }
                      onClick={() => selectTrack(timeOfDay, index)}
                    >
                      {video.title}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No items in {timeOfDay} Playlist.</p>
              )}
            </div>
          );
        })}
      </div>
      <div className="playlist-controls playlist-controls--shared">
        <div className="playlist-controls__meta">
          {selectedTimeOfDay ? `Selected: ${selectedTimeOfDay}` : 'Select an item to edit'}
        </div>
        <button
          onClick={() => moveSelected(-1)}
          disabled={linkCheck.checking || !selectedTimeOfDay || selectedIndex === null || selectedIndex === 0}
        >
          Move Up
        </button>
        <button
          onClick={() => moveSelected(1)}
          disabled={
            linkCheck.checking ||
            !selectedTimeOfDay ||
            selectedIndex === null ||
            selectedIndex >= selectedPlaylist.length - 1
          }
        >
          Move Down
        </button>
        <button
          onClick={removeSelected}
          disabled={linkCheck.checking || !selectedTimeOfDay || selectedIndex === null}
        >
          Remove
        </button>
        <button
          onClick={shuffleSelectedPlaylist}
          disabled={linkCheck.checking || !selectedTimeOfDay || selectedIndex === null}
        >
          Shuffle Playlist
        </button>
        {selectedTimeOfDay && selectedIndex !== null && (
          <button onClick={checkSelectedPlaylistLinks} disabled={linkCheck.checking}>
            {linkCheck.checking ? 'Checking...' : 'Check Links'}
          </button>
        )}
        <button
          onClick={() => clearPlaylist(selectedTimeOfDay)}
          disabled={linkCheck.checking || !selectedTimeOfDay || selectedIndex === null}
        >
          Clear Playlist
        </button>
        {linkCheck.message && (
          <div className="playlist-link-check-status" aria-live="polite">
            {linkCheck.checking && <span className="playlist-link-check-spinner" />}
            <span>{linkCheck.message}</span>
          </div>
        )}
      </div>

      <div className="add-stream-section">
        <h3>Add Content to Fallback Playlists</h3>
        <p>
          Search and add content from YouTube for when nothing else is scheduled.
        </p>
        <MediaManager
          playlistName={currentPlaylist ? currentPlaylist.name : 'New Playlist'}
          existingPlaylist={existingPlaylist}
          onSavePlaylist={onSavePlaylist}
          onUpdatePlaylist={currentPlaylist ? onUpdatePlaylist : null}
          onDeletePlaylist={currentPlaylist ? onDeletePlaylist : null}
          onLoadSavedPlaylist={onLoadSavedPlaylist}
          savedPlaylists={savedPlaylists}
          fallbackMode={true}
          deviceType={deviceType}
          categoryFixed={deviceType !== 'TV'}
          onAddToFallback={handleAddToFallback}
          siteId={siteId}
          showPlaylist={false}
        />
      </div>
    </div>
  );
}

export default FallbackPlaylist;
